import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, candidateApi } from '../lib/apiClient';
import { useAuth } from './AuthContext';

/**
 * Caches the set of job IDs the signed-in candidate has applied to, hydrated
 * from the authenticated `/candidates/me/applications` endpoint. Lets listing-
 * style cards (Saved Jobs, Jobs listing) show the real "Applied" state without
 * each card making its own per-job probe.
 *
 * Architectural sibling of `SavedJobsContext` — same auth-gating, anonymous
 * no-op, optimistic-update posture. Loaded once at app boot after auth
 * resolves. The detail page has its own `11b0287`-era `locallyApplied` flag
 * for its own button; this provider supplements it across listing pages.
 *
 * Closes the documented `11b0287` follow-up for listing-style pages.
 */

interface AppliedJobsApi {
  /** Hydrated set, or `null` while still loading after auth resolves. Empty
   *  for anonymous users. Consumers should treat `null` as "unknown". */
  appliedJobIds: Set<string> | null;
  /** True if the given jobId is in the applied set. Returns `false` while the
   *  set is still null/loading — listing cards stay in their default state. */
  isJobApplied: (jobId: string) => boolean;
  /** Optimistically adds a jobId to the set so all consumers re-render
   *  immediately after a successful apply, without waiting for a refetch. */
  markApplied: (jobId: string) => void;
  /** Re-fetches the full applications list from the server. */
  refresh: () => Promise<void>;
}

const AppliedJobsContext = createContext<AppliedJobsApi | null>(null);

export function useAppliedJobs(): AppliedJobsApi {
  const ctx = useContext(AppliedJobsContext);
  if (!ctx) throw new Error('useAppliedJobs must be used within <AppliedJobsProvider>');
  return ctx;
}

const APPLIED_FETCH_PAGE_SIZE = 100;

export function AppliedJobsProvider({ children }: { children: ReactNode }) {
  const { candidate } = useAuth();
  const [appliedJobIds, setAppliedJobIds] = useState<Set<string> | null>(null);
  const lastCandidateIdRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!candidate) {
      setAppliedJobIds(new Set());
      return;
    }
    try {
      // Full pagination — loop pages until we've consumed all of them. Backend
      // caps page_size at 100, so a candidate with <100 applications finishes
      // in a single request (the loop checks meta.total_pages and stops).
      // Termination: meta.page >= meta.total_pages (no has_more field on the
      // pagination envelope; total_pages is authoritative).
      const ids = new Set<string>();
      let page = 1;
      while (true) {
        const env = await candidateApi.applications.list({
          page,
          page_size: APPLIED_FETCH_PAGE_SIZE,
        });
        for (const app of env.data) {
          const jobId = app.job?.id;
          if (jobId) ids.add(jobId);
        }
        const totalPages = env.meta?.total_pages ?? 1;
        if (page >= totalPages) break;
        page += 1;
      }
      setAppliedJobIds(ids);
    } catch (err) {
      // Silent failure — applied-state cache is best-effort. Cards fall back to
      // their default "not applied" rendering. A real ALREADY_APPLIED is still
      // surfaced server-side during apply submission as a 409, so behavior
      // degrades to the pre-fix UX rather than into a broken state.
      if (!(err instanceof ApiError)) throw err;
      setAppliedJobIds(new Set());
    }
  }, [candidate]);

  useEffect(() => {
    const currentId = candidate?.id ?? null;
    if (lastCandidateIdRef.current === currentId) return;
    lastCandidateIdRef.current = currentId;
    if (!candidate) {
      setAppliedJobIds(new Set());
      return;
    }
    setAppliedJobIds(null);
    void refresh();
  }, [candidate, refresh]);

  const isJobApplied = useCallback(
    (jobId: string): boolean => {
      if (!appliedJobIds) return false;
      return appliedJobIds.has(jobId);
    },
    [appliedJobIds],
  );

  const markApplied = useCallback((jobId: string) => {
    setAppliedJobIds((prev) => {
      if (prev?.has(jobId)) return prev;
      const next = new Set(prev ?? []);
      next.add(jobId);
      return next;
    });
  }, []);

  const value = useMemo<AppliedJobsApi>(
    () => ({ appliedJobIds, isJobApplied, markApplied, refresh }),
    [appliedJobIds, isJobApplied, markApplied, refresh],
  );

  return <AppliedJobsContext.Provider value={value}>{children}</AppliedJobsContext.Provider>;
}
